import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, CaretLeft, CaretRight, ChatCircle, MapPin, Trash, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ListingDetailPage() {
  const { listingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [msgText, setMsgText] = useState('');
  const [showMsg, setShowMsg] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/listings/${listingId}`)
      .then(({ data }) => setListing(data))
      .catch(() => toast.error('Listing not found'))
      .finally(() => setLoading(false));
  }, [listingId]);

  const handleSave = async () => {
    if (!user) { navigate('/auth'); return; }
    const { data } = await api.post(`/listings/${listingId}/save`);
    setListing(prev => ({
      ...prev,
      saved_by: data.saved
        ? [...(prev.saved_by || []), user.user_id]
        : (prev.saved_by || []).filter(id => id !== user.user_id)
    }));
  };

  const handleSendMessage = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!msgText.trim()) return;
    setSending(true);
    try {
      await api.post('/messages', {
        listing_id: listing.listing_id,
        receiver_id: listing.seller_id,
        text: msgText.trim()
      });
      toast.success('Message sent!');
      setMsgText('');
      setShowMsg(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await api.delete(`/listings/${listingId}`);
      toast.success('Listing deleted');
      navigate('/shop');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) {
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background"><div className="animate-pulse w-8 h-8 rounded-full bg-muted" /></div>;
  }

  if (!listing) {
    return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background"><p className="text-muted-foreground font-sans">Listing not found</p></div>;
  }

  const photos = listing.photos || [];
  const isSaved = (listing.saved_by || []).includes(user?.user_id);
  const isOwner = user?.user_id === listing.seller_id;
  const isAdmin = user?.role === 'admin';
  const canDelete = isOwner || isAdmin;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="listing-detail-page">
      {/* Back btn */}
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <button onClick={() => navigate('/shop')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground font-sans transition-colors" data-testid="back-to-shop-btn">
          <ArrowLeft size={16} /> Back to Shop
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Photo gallery */}
        {photos.length > 0 ? (
          <div className="relative aspect-square sm:aspect-[4/3] bg-black rounded-sm overflow-hidden" data-testid="listing-gallery">
            <img
              src={`${API_BASE}/api/media/${photos[photoIdx]}`}
              alt={listing.title}
              className="w-full h-full object-contain"
            />
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setPhotoIdx(i => Math.max(0, i - 1))}
                  disabled={photoIdx === 0}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur rounded-full flex items-center justify-center disabled:opacity-30"
                  data-testid="gallery-prev"
                >
                  <CaretLeft size={16} />
                </button>
                <button
                  onClick={() => setPhotoIdx(i => Math.min(photos.length - 1, i + 1))}
                  disabled={photoIdx >= photos.length - 1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 backdrop-blur rounded-full flex items-center justify-center disabled:opacity-30"
                  data-testid="gallery-next"
                >
                  <CaretRight size={16} />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {photos.map((_, i) => (
                    <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === photoIdx ? 'bg-white' : 'bg-white/40'}`} />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="aspect-square bg-muted flex items-center justify-center rounded-sm">
            <p className="text-muted-foreground text-sm font-sans">No photos</p>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-3xl font-serif font-semibold text-primary" data-testid="listing-price">${listing.price?.toFixed(2)}</p>
              <h1 className="text-lg font-sans font-semibold text-foreground mt-1" data-testid="listing-title">{listing.title}</h1>
            </div>
            <button onClick={handleSave} className="p-2" data-testid="listing-save-btn">
              <Heart size={24} weight={isSaved ? 'fill' : 'regular'} className={isSaved ? 'text-red-500' : 'text-muted-foreground'} />
            </button>
          </div>

          <div className="flex gap-2">
            <Badge variant="outline" className="rounded-sm text-xs capitalize">{listing.category}</Badge>
            <Badge variant="secondary" className="rounded-sm text-xs capitalize">{listing.condition}</Badge>
          </div>

          {listing.location && (
            <p className="text-sm text-muted-foreground font-sans flex items-center gap-1">
              <MapPin size={14} /> {listing.location}
            </p>
          )}

          {listing.description && (
            <div className="border-t border-border pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-sans font-semibold mb-2">Description</p>
              <p className="text-sm text-foreground font-sans whitespace-pre-wrap">{listing.description}</p>
            </div>
          )}

          {/* Seller */}
          <div className="border-t border-border pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-sans font-semibold mb-2">Seller</p>
            <button
              onClick={() => navigate(`/profile/${listing.seller_id}`)}
              className="text-sm font-sans font-semibold text-primary hover:text-[#083313] transition-colors"
              data-testid="listing-seller-link"
            >
              {listing.seller_name || 'Golfer'}
            </button>
            <p className="text-xs text-muted-foreground font-sans mt-1">
              Listed {new Date(listing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Actions */}
          <div className="border-t border-border pt-4 space-y-3">
            {!isOwner && (
              <>
                <Button
                  onClick={() => setShowMsg(true)}
                  className="w-full h-12 rounded-sm bg-primary hover:bg-[#083313] gap-2 text-sm tracking-wide uppercase font-semibold font-sans"
                  data-testid="message-seller-btn"
                >
                  <ChatCircle size={18} weight="bold" /> Message Seller
                </Button>
                {showMsg && (
                  <div className="space-y-2" data-testid="message-form">
                    <textarea
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      placeholder="Hi, is this still available?"
                      rows={3}
                      className="w-full bg-transparent border border-border rounded-sm focus:border-primary focus:outline-none focus:ring-0 px-3 py-2 text-sm font-sans resize-none"
                      data-testid="message-input"
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowMsg(false)} className="rounded-sm flex-1">Cancel</Button>
                      <Button onClick={handleSendMessage} disabled={sending} className="rounded-sm bg-primary flex-1" data-testid="send-message-btn">
                        {sending ? 'Sending...' : 'Send'}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {canDelete && (
              <Button variant="outline" onClick={handleDelete} className="w-full rounded-sm text-destructive border-destructive/30 hover:bg-destructive/5 gap-2" data-testid="delete-listing-btn">
                <Trash size={16} /> Delete Listing
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
